<template>
  <div class="manual-readings">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">
              <i class="fas fa-pencil-ruler mr-2"></i>
              Manual Readings
            </h4>
            <b-button v-if="featureEnabled" variant="success" size="sm" :disabled="!devices || !devices.length" @click="openCreateModal">
              <i class="fas fa-plus mr-1"></i> Add Reading
            </b-button>
          </div>
          <div class="card-body">

            <b-alert v-if="!featureEnabled" show variant="info" class="mb-0">
              <i class="fa fa-info-circle mr-1"></i>
              <strong>Backend support pending.</strong>
              Manual readings require the <code>ManualReading</code> GraphQL type and REST endpoints on <code>leafsvc</code>.
              Once the API is deployed, set <code>VUE_APP_MANUAL_READINGS_ENABLED=1</code> and rebuild the dashboard to enable this page.
            </b-alert>

            <template v-else>

            <!-- Filters -->
            <b-row class="mb-3">
              <b-col md="4">
                <b-form-group label="Device" label-for="filter-device" class="mb-0">
                  <b-form-select
                    id="filter-device"
                    v-model="filters.deviceId"
                    :options="deviceFilterOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Metric" label-for="filter-metric" class="mb-0">
                  <b-form-select
                    id="filter-metric"
                    v-model="filters.metricName"
                    :options="metricFilterOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Source" label-for="filter-source" class="mb-0">
                  <b-form-select
                    id="filter-source"
                    v-model="filters.source"
                    :options="sourceOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="&nbsp;" class="mb-0">
                  <b-button variant="outline-secondary" size="sm" block @click="resetFilters">
                    <i class="fa fa-times mr-1"></i> Clear
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>

            <div v-if="$apollo.loading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>

            <div v-else-if="manualReadings && manualReadings.length" class="table-responsive">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Device</th>
                    <th>Metric</th>
                    <th>Value</th>
                    <th class="d-none d-md-table-cell">Unit</th>
                    <th>Type</th>
                    <th class="d-none d-lg-table-cell">Notes</th>
                    <th class="d-none d-lg-table-cell">Created By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in manualReadings" :key="r.id">
                    <td>{{ formatTime(r.timestamp) }}</td>
                    <td><strong>{{ r.device ? r.device.name : '—' }}</strong></td>
                    <td><code>{{ r.metricName }}</code></td>
                    <td>{{ r.value }}</td>
                    <td class="d-none d-md-table-cell">{{ r.unit || '—' }}</td>
                    <td>
                      <b-badge v-if="r.source === 'import'" variant="info">Import</b-badge>
                      <b-badge v-else-if="r.isFieldVerified" variant="success">Field Verified</b-badge>
                      <b-badge v-else variant="warning">Manual</b-badge>
                    </td>
                    <td class="d-none d-lg-table-cell text-truncate" style="max-width:220px;">{{ r.notes || '—' }}</td>
                    <td class="d-none d-lg-table-cell">
                      <span v-if="r.createdBy">{{ r.createdBy.firstName }} {{ r.createdBy.lastName }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-right">
                      <b-dropdown size="sm" variant="outline-secondary" right no-caret boundary="viewport">
                        <template #button-content>
                          <i class="fas fa-ellipsis-v"></i>
                        </template>
                        <b-dropdown-item @click="openEditModal(r)">
                          <i class="fas fa-edit mr-1"></i> Edit
                        </b-dropdown-item>
                        <b-dropdown-item variant="danger" @click="confirmDelete(r)">
                          <i class="fas fa-trash mr-1"></i> Delete
                        </b-dropdown-item>
                      </b-dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center text-muted py-4">
              <i class="fas fa-pencil-ruler fa-2x mb-2"></i>
              <p v-if="hasActiveFilters">No readings match the current filters.</p>
              <p v-else>No manual readings recorded yet.</p>
            </div>

            </template>
          </div>
        </div>
      </div>
    </div>

    <ManualReadingModal
      :device="modalDevice"
      :reading="modalReading"
      :visible="showModal"
      @close="onModalClosed"
      @created="onSaved"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import moment from 'moment-timezone'
import {
  MANUAL_READINGS_QUERY,
  DELETE_MANUAL_READING_MUTATION,
  ALL_DEVICES_QUERY
} from '@/constants/graphql'
import { DEVICE_METRICS } from '@/constants/data'
import { MANUAL_READINGS_ENABLED } from '@/constants/features'
import ManualReadingModal from '@/components/ManualReadingModal.vue'

export default {
  name: 'ManualReadings',
  components: { ManualReadingModal },
  data () {
    return {
      filters: {
        deviceId: null,
        metricName: null,
        source: null
      },
      showModal: false,
      modalDevice: null,
      modalReading: null,
      sourceOptions: [
        { value: null, text: 'All sources' },
        { value: 'manual', text: 'Manual entries' },
        { value: 'import', text: 'CSV imports' }
      ]
    }
  },
  apollo: {
    devices: {
      query: ALL_DEVICES_QUERY
    },
    manualReadings: {
      query: MANUAL_READINGS_QUERY,
      variables () {
        const where = {}
        if (this.filters.deviceId) where.deviceId = this.filters.deviceId
        if (this.filters.metricName) where.metricName = this.filters.metricName
        if (this.filters.source) where.source = this.filters.source
        return {
          where,
          orderBy: { timestamp: 'desc' },
          limit: 500
        }
      },
      skip () {
        return !MANUAL_READINGS_ENABLED
      },
      fetchPolicy: 'cache-and-network'
    }
  },
  computed: {
    featureEnabled () { return MANUAL_READINGS_ENABLED },
    deviceFilterOptions () {
      const base = [{ value: null, text: 'All devices' }]
      if (!this.devices) return base
      return base.concat(this.devices.map(d => ({
        value: d.id,
        text: `${d.name} (${d.serial})`
      })))
    },
    metricFilterOptions () {
      const seen = new Set()
      const list = []
      Object.values(DEVICE_METRICS).forEach(arr => {
        arr.forEach(m => {
          if (!seen.has(m.value)) {
            seen.add(m.value)
            list.push({ value: m.value, text: m.text })
          }
        })
      })
      return [{ value: null, text: 'All metrics' }].concat(list)
    },
    hasActiveFilters () {
      return !!(this.filters.deviceId || this.filters.metricName || this.filters.source)
    },
    devicesById () {
      const map = {}
      ;(this.devices || []).forEach(d => { map[d.id] = d })
      return map
    }
  },
  methods: {
    formatTime (ts) {
      if (!ts) return '—'
      return moment(ts).format('YYYY-MM-DD HH:mm')
    },
    resetFilters () {
      this.filters = { deviceId: null, metricName: null, source: null }
    },
    openCreateModal () {
      // pick the filter device if set, otherwise the first device
      const targetId = this.filters.deviceId || (this.devices && this.devices[0] && this.devices[0].id)
      this.modalDevice = this.devicesById[targetId] || null
      this.modalReading = null
      this.showModal = true
    },
    openEditModal (reading) {
      this.modalDevice = (reading.device && this.devicesById[reading.device.id]) || reading.device || null
      this.modalReading = reading
      this.showModal = true
    },
    onModalClosed () {
      this.showModal = false
      this.modalReading = null
    },
    onSaved () {
      this.showModal = false
      this.modalReading = null
      this.$apollo.queries.manualReadings.refetch()
    },
    confirmDelete (reading) {
      const label = reading.device ? reading.device.name : 'this device'
      this.$swal.fire({
        title: 'Delete reading?',
        text: `Delete the ${reading.metricName} = ${reading.value} reading for ${label} on ${this.formatTime(reading.timestamp)}?`,
        icon: 'warning',
        toast: false,
        position: 'center',
        showCancelButton: true,
        showConfirmButton: true,
        timer: 0,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc3545'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$apollo.mutate({
          mutation: DELETE_MANUAL_READING_MUTATION,
          variables: { id: reading.id }
        }).then(() => {
          this.$swal.fire({ icon: 'success', title: 'Reading deleted.' })
          this.$apollo.queries.manualReadings.refetch()
        }).catch(err => {
          // eslint-disable-next-line no-console
          console.error('Manual reading delete failed:', err)
          this.$swal.fire({
            icon: 'error',
            title: 'Failed to delete reading',
            text: err.message || 'An unexpected error occurred.',
            toast: false,
            position: 'center',
            showConfirmButton: true,
            timer: 0
          })
        })
      })
    }
  }
}
</script>

<style scoped>
.manual-readings .card-title {
  font-size: 1.1rem;
}
</style>
